import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [logs, setLogs] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('users');
  const [userSessions, setUserSessions] = useState([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAddAdminModal, setShowAddAdminModal] = useState(false);
  const [showEditAdminModal, setShowEditAdminModal] = useState(false);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [selectedUserToPromote, setSelectedUserToPromote] = useState(null);
  const [promoteFormData, setPromoteFormData] = useState({
    username: '',
    password: '',
    confirmPassword: '',
    full_name: ''
  });
  const navigate = useNavigate();

  const [newUser, setNewUser] = useState({
    user_id: '',
    user_name: '',
    email: '',
    phone: '',
    days_allowed: 30,
    is_active: true,
    notes: ''
  });

  const [newAdmin, setNewAdmin] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    is_active: true
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchData();
  }, [navigate]);

  useEffect(() => {
    if (activeTab === 'sessions') {
      fetchUserSessions();
    }
  }, [activeTab]);

  const fetchUserSessions = async () => {
    setLoadingSessions(true);
    try {
      const token = localStorage.getItem('adminToken');
      const res = await axios.get(`${API_URL}/api/admin/user-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserSessions(res.data.data || []);
    } catch (error) {
      setUserSessions([]);
    }
    setLoadingSessions(false);
  };

  const getAuthHeaders = () => {
    const token = localStorage.getItem('adminToken');
    return {
      headers: { Authorization: `Bearer ${token}` }
    };
  };

  const fetchData = async () => {
    try {
      const [usersRes, statsRes, logsRes, adminsRes] = await Promise.all([
        axios.get(`${API_URL}/api/user-access/admin/users`, getAuthHeaders()),
        axios.get(`${API_URL}/api/user-access/admin/statistics`, getAuthHeaders()),
        axios.get(`${API_URL}/api/user-access/admin/logs?limit=50`, getAuthHeaders()),
        axios.get(`${API_URL}/api/user-access/admin/admins`, getAuthHeaders())
      ]);

      setUsers(usersRes.data.users);
      setStatistics(statsRes.data.statistics);
      setLogs(logsRes.data.logs);
      setAdmins(adminsRes.data.admins);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin/login');
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    navigate('/admin/login');
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/user-access/admin/users`, newUser, getAuthHeaders());
      setShowAddModal(false);
      setNewUser({
        user_id: '',
        user_name: '',
        email: '',
        phone: '',
        days_allowed: 30,
        is_active: true,
        notes: ''
      });
      fetchData();
      alert('User added successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Error adding user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API_URL}/api/user-access/admin/users/${selectedUser.user_id}`,
        selectedUser,
        getAuthHeaders()
      );
      setShowEditModal(false);
      setSelectedUser(null);
      fetchData();
      alert('User updated successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Error updating user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      await axios.delete(`${API_URL}/api/user-access/admin/users/${userId}`, getAuthHeaders());
      fetchData();
      alert('User deleted successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Error deleting user');
    }
  };

  const handleExtendAccess = async (userId) => {
    const days = prompt('Enter number of days to extend:');
    if (!days || isNaN(days)) return;

    try {
      await axios.post(
        `${API_URL}/api/user-access/admin/users/${userId}/extend`,
        { days: parseInt(days) },
        getAuthHeaders()
      );
      fetchData();
      alert(`Access extended by ${days} days!`);
    } catch (error) {
      alert(error.response?.data?.message || 'Error extending access');
    }
  };

  const handleToggleStatus = async (user) => {
    try {
      await axios.put(
        `${API_URL}/api/user-access/admin/users/${user.user_id}`,
        { is_active: !user.is_active },
        getAuthHeaders()
      );
      fetchData();
    } catch (error) {
      alert(error.response?.data?.message || 'Error updating status');
    }
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/api/user-access/admin/admins`, newAdmin, getAuthHeaders());
      setShowAddAdminModal(false);
      setNewAdmin({ username: '', password: '', full_name: '', email: '', is_active: true });
      fetchData();
      alert('Admin user created successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Error creating admin user');
    }
  };

  const handleUpdateAdmin = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${API_URL}/api/user-access/admin/admins/${selectedAdmin.id}`,
        selectedAdmin,
        getAuthHeaders()
      );
      setShowEditAdminModal(false);
      setSelectedAdmin(null);
      fetchData();
      alert('Admin user updated successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Error updating admin user');
    }
  };

  const handleDeleteAdmin = async (adminId) => {
    if (!window.confirm('Are you sure you want to delete this admin user?')) return;

    try {
      await axios.delete(`${API_URL}/api/user-access/admin/admins/${adminId}`, getAuthHeaders());
      fetchData();
      alert('Admin user deleted successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Error deleting admin user');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match!');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/api/user-access/admin/change-password`,
        {
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        },
        getAuthHeaders()
      );
      setShowChangePasswordModal(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      alert('Password changed successfully!');
    } catch (error) {
      alert(error.response?.data?.message || 'Error changing password');
    }
  };

  const handlePromoteUserClick = (user) => {
    setSelectedUserToPromote(user);
    setPromoteFormData({
      username: `${user.user_id}_admin`,
      password: '',
      confirmPassword: '',
      full_name: user.user_name || ''
    });
    setShowPromoteModal(true);
  };

  const handlePromoteUser = async (e) => {
    e.preventDefault();

    if (!promoteFormData.username || !promoteFormData.password) {
      alert('Username and password are required!');
      return;
    }

    if (promoteFormData.password !== promoteFormData.confirmPassword) {
      alert('Passwords do not match!');
      return;
    }

    if (promoteFormData.password.length < 6) {
      alert('Password must be at least 6 characters long!');
      return;
    }

    try {
      const response = await axios.post(
        `${API_URL}/api/user-access/admin/promote-user`,
        {
          userId: selectedUserToPromote.user_id,
          username: promoteFormData.username,
          password: promoteFormData.password,
          full_name: promoteFormData.full_name
        },
        getAuthHeaders()
      );
      setShowPromoteModal(false);
      setSelectedUserToPromote(null);
      setPromoteFormData({ username: '', password: '', confirmPassword: '', full_name: '' });
      fetchData();
      alert(`User ${selectedUserToPromote.user_id} has been promoted to admin! Admin Username: ${promoteFormData.username}`);
    } catch (error) {
      alert(error.response?.data?.message || 'Error promoting user');
    }
  };

  const filteredUsers = users.filter(user => {
    if (filterStatus === 'all') return true;
    return user.status === filterStatus;
  });

  const getStatusBadge = (status) => {
    const badges = {
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      expired: 'bg-red-100 text-red-800'
    };
    return badges[status] || badges.inactive;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold">Admin Dashboard</h1>
              <p className="text-blue-100 text-sm">User Access Control Panel</p>
            </div>
            <button
              onClick={handleLogout}
              className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg font-semibold transition-all"
            >
              Logout
            </button>
          </div>
        </div>
      </div>

      {statistics && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Users</div>
              <div className="text-2xl font-bold text-gray-900">{statistics.total_users}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Active</div>
              <div className="text-2xl font-bold text-green-600">{statistics.active_users}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Inactive</div>
              <div className="text-2xl font-bold text-gray-600">{statistics.inactive_users}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Expired</div>
              <div className="text-2xl font-bold text-red-600">{statistics.expired_users}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Today</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.users_logged_today}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Logins</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.total_logins}</div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <div className="flex space-x-8 px-6">
              <button
                onClick={() => setActiveTab('users')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'users'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Users Management
              </button>
              <button
                onClick={() => setActiveTab('logs')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'logs'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Login Logs
              </button>
              <button
                onClick={() => setActiveTab('admins')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'admins'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Admin Users
              </button>
              <button
                onClick={() => setActiveTab('sessions')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'sessions'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                User Sessions
              </button>
            </div>
          </div>

          {activeTab === 'users' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex space-x-2">
                  <button
                    onClick={() => setFilterStatus('all')}
                    className={`px-4 py-2 rounded-lg ${filterStatus === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setFilterStatus('active')}
                    className={`px-4 py-2 rounded-lg ${filterStatus === 'active' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                  >
                    Active
                  </button>
                  <button
                    onClick={() => setFilterStatus('expired')}
                    className={`px-4 py-2 rounded-lg ${filterStatus === 'expired' ? 'bg-red-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                  >
                    Expired
                  </button>
                  <button
                    onClick={() => setFilterStatus('inactive')}
                    className={`px-4 py-2 rounded-lg ${filterStatus === 'inactive' ? 'bg-gray-500 text-white' : 'bg-gray-200 text-gray-700'}`}
                  >
                    Inactive
                  </button>
                </div>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold"
                >
                  + Add User
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expiry Date</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Left</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Login</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Logins</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredUsers.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{user.user_id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.user_name || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.email || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadge(user.status)}`}>
                            {user.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(user.access_expiry_date).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.days_remaining > 0 ? `${user.days_remaining} days` : 'Expired'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {user.last_login ? new Date(user.last_login).toLocaleString() : 'Never'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{user.login_count}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => {
                              setSelectedUser(user);
                              setShowEditModal(true);
                            }}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleToggleStatus(user)}
                            className={user.is_active ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}
                          >
                            {user.is_active ? 'Deactivate' : 'Activate'}
                          </button>
                          <button
                            onClick={() => handleExtendAccess(user.user_id)}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            Extend
                          </button>
                          <button
                            onClick={() => handlePromoteUserClick(user)}
                            className="text-indigo-600 hover:text-indigo-900 font-semibold"
                            title="Make this user an admin"
                          >
                            👤 Promote
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.user_id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'logs' && (
            <div className="p-6">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User ID</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(log.login_time).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{log.user_id}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            log.login_status === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {log.login_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'admins' && (
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold">Administrator Accounts</h3>
                <button
                  onClick={() => setShowAddAdminModal(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg font-semibold"
                >
                  + Add Admin
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Full Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {admins.map((admin) => (
                      <tr key={admin.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{admin.username}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{admin.full_name || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{admin.email || '-'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleDeleteAdmin(admin.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {activeTab === 'sessions' && (
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Active User Sessions</h3>
              {loadingSessions ? (
                <div className="text-gray-500">Loading sessions...</div>
              ) : userSessions.length === 0 ? (
                <div className="text-gray-500">No user sessions found.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">User ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Session ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Client ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Expires At</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Updated</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {userSessions.map((session) => (
                        <tr key={session.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{session.user_id}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 truncate max-w-xs">{session.user_session || '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{session.client_id}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{session.expires_at ? new Date(session.expires_at).toLocaleString() : '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{session.created_at ? new Date(session.created_at).toLocaleString() : '-'}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{session.updated_at ? new Date(session.updated_at).toLocaleString() : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">Add New User</h3>
            <form onSubmit={handleAddUser} className="space-y-4">
              <input type="text" placeholder="User ID" value={newUser.user_id} onChange={(e) => setNewUser({...newUser, user_id: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
              <input type="text" placeholder="Name" value={newUser.user_name} onChange={(e) => setNewUser({...newUser, user_name: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              <input type="email" placeholder="Email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              <input type="number" placeholder="Days" value={newUser.days_allowed} onChange={(e) => setNewUser({...newUser, days_allowed: parseInt(e.target.value)})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required min="1" />
              <div className="flex space-x-3">
                <button type="submit" className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg font-semibold">Add</button>
                <button type="button" onClick={() => setShowAddModal(false)} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">Edit User</h3>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <input type="text" value={selectedUser.user_name || ''} onChange={(e) => setSelectedUser({...selectedUser, user_name: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Name" />
              <input type="email" value={selectedUser.email || ''} onChange={(e) => setSelectedUser({...selectedUser, email: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Email" />
              <div className="flex space-x-3">
                <button type="submit" className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg font-semibold">Update</button>
                <button type="button" onClick={() => {setShowEditModal(false); setSelectedUser(null);}} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAddAdminModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-4">Add New Admin</h3>
            <form onSubmit={handleAddAdmin} className="space-y-4">
              <input type="text" placeholder="Username" value={newAdmin.username} onChange={(e) => setNewAdmin({...newAdmin, username: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required />
              <input type="password" placeholder="Password" value={newAdmin.password} onChange={(e) => setNewAdmin({...newAdmin, password: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" required minLength="6" />
              <input type="text" placeholder="Full Name" value={newAdmin.full_name} onChange={(e) => setNewAdmin({...newAdmin, full_name: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              <input type="email" placeholder="Email" value={newAdmin.email} onChange={(e) => setNewAdmin({...newAdmin, email: e.target.value})} className="w-full px-3 py-2 border border-gray-300 rounded-lg" />
              <div className="flex space-x-3">
                <button type="submit" className="flex-1 bg-blue-500 hover:bg-blue-600 text-white py-2 rounded-lg font-semibold">Create</button>
                <button type="button" onClick={() => setShowAddAdminModal(false)} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showPromoteModal && selectedUserToPromote && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-bold mb-2">Promote User to Admin</h3>
            <p className="text-sm text-gray-600 mb-4">
              User ID: <strong>{selectedUserToPromote.user_id}</strong> ({selectedUserToPromote.user_name})
            </p>
            <form onSubmit={handlePromoteUser} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Username</label>
                <input 
                  type="text" 
                  placeholder="Admin username" 
                  value={promoteFormData.username} 
                  onChange={(e) => setPromoteFormData({...promoteFormData, username: e.target.value})} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" 
                  required 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Password</label>
                <input 
                  type="password" 
                  placeholder="Admin password" 
                  value={promoteFormData.password} 
                  onChange={(e) => setPromoteFormData({...promoteFormData, password: e.target.value})} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" 
                  required 
                  minLength="6"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
                <input 
                  type="password" 
                  placeholder="Confirm password" 
                  value={promoteFormData.confirmPassword} 
                  onChange={(e) => setPromoteFormData({...promoteFormData, confirmPassword: e.target.value})} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" 
                  required 
                  minLength="6"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
                <input 
                  type="text" 
                  placeholder="Full name" 
                  value={promoteFormData.full_name} 
                  onChange={(e) => setPromoteFormData({...promoteFormData, full_name: e.target.value})} 
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg" 
                />
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm text-yellow-800">
                ⚠️ After promotion, the user can login with the admin username and password you set here and will have full admin access.
              </div>
              <div className="flex space-x-3">
                <button type="submit" className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white py-2 rounded-lg font-semibold">Promote</button>
                <button type="button" onClick={() => setShowPromoteModal(false)} className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-800 py-2 rounded-lg font-semibold">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
